import React from "react";
import { CreateFunction, UpdateFunction } from "@hex-insights/app-modules";
import { ClassNameProps, DistributiveOmit, Heading, HeadingLevel, Section, StyleProps } from "@hex-insights/core";
import {
	Form,
	FormEditButtonPair,
	FormState,
	SubmitButton,
	useInternalField,
	useInternalFields,
} from "@hex-insights/forms";
import { EmployeeCreateMutation, EmployeeDetailQuery, EmployeeUpdateMutation } from "../../../Utilities";
import { EmployeeFormState, EmployeeFormValues } from "../../FormUtilities";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import {
	useCreateFormControl,
	useCreateFormSubmission,
	useDetailFormControl,
	useDetailFormSubmission,
	useReadOnlyForm,
} from "./hooks";

export {
	type UseCreateFormArgs,
	useCreateForm,
	type UseDetailFormArgs,
	useDetailForm,
	type UseReadOnlyFormArgs,
	useReadOnlyForm,
} from "./hooks";

export type ControlledCreateProps = Omit<
	CreateProps<EmployeeCreateMutation["createEmployee"]>,
	"formState" | "create"
> & {
	formStateArgs?: EmployeeFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the Employee model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

export type CreateProps<T> = {
	formState: FormState<EmployeeFormValues.Create>;
	create: CreateFunction<EmployeeFormValues.Create, T>;
	onSuccess: (formState: FormState<EmployeeFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
	columnStyle?: React.CSSProperties;
} & BaseFormProps<EmployeeFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the Employee model using the given form state.
 */
export function Create<T>({ formState, create, onSuccess, formNameSuffix, columnStyle, ...props }: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "employee.create" + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column style={columnStyle}>
				<CreateFields formState={formState} />

				<Form.ActionBar>
					<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
						Submit
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

const enrollmentFieldNames: (keyof EmployeeFormValues.Create)[] = [
	"teacherCourseSectionEnrollments",
	"teacherHomeRoomSectionEnrollments",
];

type CreateFieldsProps = {
	formState: FormState<EmployeeFormValues.Create>;
	headingLevel?: HeadingLevel;
};

function CreateFields({ formState, headingLevel = 3 }: CreateFieldsProps) {
	useInternalFields(formState, enrollmentFieldNames);

	return (
		<React.Fragment>
			<Field.Person.SubForm formState={formState} style={{ width: "100%" }} />

			<Section>
				<Section.Header>
					<Heading level={headingLevel} noMargin>
						Employment Details
					</Heading>
				</Section.Header>
				<Section.Body>
					<Form.Column>
						<Field.WorkEmailAddress formState={formState} />
						<Field.JobTitle formState={formState} />
						<Field.Role formState={formState} />
						<Field.StartDate formState={formState} />
						<Field.EndDate formState={formState} />
						<Field.Bio formState={formState} />
						<Field.Campuses.Select formState={formState} />
						<Field.ClassYearCategories.Select formState={formState} />
						<Field.ClassYears.Select formState={formState} />
					</Form.Column>
				</Section.Body>
			</Section>
		</React.Fragment>
	);
}

export type ControlledDetailProps = Omit<
	DetailProps<EmployeeUpdateMutation["updateEmployee"]>,
	"formState" | "update"
> & {
	formStateArgs?: Partial<DistributiveOmit<EmployeeFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

/**
 * Detail form of the Employee model using an internally managed form state.
 */
export function ControlledDetail({ formStateArgs, ...props }: ControlledDetailProps) {
	const { formState, update } = useDetailFormControl({ ...formStateArgs, employee: props.employee });

	return <Detail formState={formState} update={update} {...props} />;
}

export type DetailProps<T> = {
	employee: EmployeeDetailQuery["employee"];
	formState: FormState<EmployeeFormValues.Detail>;
	update: UpdateFunction<EmployeeFormValues.Detail, T>;
	onSuccess: (formState: FormState<EmployeeFormValues.Detail>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<EmployeeFormValues.Detail> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Detail form of the Employee model using the given form state.
 */
export function Detail<T>({ formState, employee, update, onSuccess, formNameSuffix, ...props }: DetailProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName = `employee.detail.${employee.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<FormEditButtonPair formState={formState} />

				<DetailFields formState={formState} employee={employee} />
			</Form.Column>
		</Form>
	);
}

export type ControlledReadOnlyProps = Omit<ReadOnlyProps, "formState"> & {
	formStateArgs?: Partial<DistributiveOmit<EmployeeFormState.UseReadOnlyFormStateArgs, "initialFormValues">>;
};

/**
 * Read-only detail form of the Employee model using an internally managed form state.
 */
export function ControlledReadOnly({ formStateArgs, ...props }: ControlledReadOnlyProps) {
	const { formState } = useReadOnlyForm({ ...formStateArgs, employee: props.employee });

	return <ReadOnly formState={formState} {...props} />;
}

export type ReadOnlyProps = {
	employee: EmployeeDetailQuery["employee"];
	formState: FormState<EmployeeFormValues.Detail>;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Read-only detail form of the Employee model using the given form state.
 */
export function ReadOnly({ formState, employee, className, style }: ReadOnlyProps) {
	return (
		<div className={className} style={style}>
			<Form.Column>
				<DetailFields formState={formState} employee={employee} />
			</Form.Column>
		</div>
	);
}

type DetailFieldsProps = {
	employee: EmployeeDetailQuery["employee"];
	formState: FormState<EmployeeFormValues.Detail>;
};

function DetailFields({ formState, employee }: DetailFieldsProps) {
	useInternalField(formState, "personID");

	return (
		<React.Fragment>
			<Field.WorkEmailAddress formState={formState} />
			<Field.JobTitle formState={formState} />
			<Field.Role formState={formState} />
			<Field.StartDate formState={formState} />
			<Field.EndDate formState={formState} />
			<Field.Bio formState={formState} />
			<Field.Campuses.Select formState={formState} currentInstances={employee.campuses} />
			<Field.ClassYearCategories.Select formState={formState} currentInstances={employee.classYearCategories} />
			<Field.ClassYears.Select formState={formState} currentInstances={employee.classYears} />
		</React.Fragment>
	);
}
